import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';
import { distinctUntilChanged, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICostTypeGroupDetail } from '../../../models/cost-type-group-detail.model';
import { ICostTypeGroup } from '../../../models/cost-type-group.model';
import { ICostType } from '../../../models/cost-type.model';
import { ICostTypeState } from '../../../services/facades/master-data/cost-type-state.model';
import { CostTypeFacade } from '../../../services/facades/master-data/cost-type.facade';
import { CostTypeDialogComponent } from './cost-type-dialog/cost-type-dialog.component';
import { CostTypeGroupDialogComponent } from './cost-type-group-dialog/cost-type-group-dialog.component';

@Component({
    templateUrl: './cost-type.page.html',
    styleUrls: [ './cost-type.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class CostTypePage implements OnInit, OnDestroy {
    public dataSource: MatTableDataSource<ICostTypeGroupDetail> = new MatTableDataSource<ICostTypeGroupDetail>([]);
    public costTypeState$: Observable<ICostTypeState>;
    public isExpandAll: boolean = false;
    public isExpandedMap: Record<number, boolean> = {};
    private destroy$: Subject<void> = new Subject();

    constructor(private myCostTypeFacade: CostTypeFacade,
                private myMatDialog: MatDialog) {
    }

    public ngOnInit(): void {
        this.costTypeState$ = this.myCostTypeFacade.subState();
        this.costTypeState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ICostTypeState) => s.costTypeGroups),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: ICostTypeGroupDetail[]) => {
                this.dataSource.data = data;
                this.dataSource.filterPredicate = (group, filter: string): boolean => {
                    const accumulator = (currentTerm, key): string => {
                        return this.nestedFilterCheck(currentTerm, group, key);
                    };
                    const dataStr = Object.keys(group).reduce(accumulator, '').toLowerCase();
                    // Transform the filter by converting it to lowercase and removing whitespace.
                    const transformedFilter = filter.trim().toLowerCase();
                    return dataStr.indexOf(transformedFilter) !== -1;
                };
            });

        void this.myCostTypeFacade.fetchData();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.myCostTypeFacade.clearState();
    }

    public onOpenCostTypeDialog(costType?: ICostType): void {
        const dialog: MatDialogRef<CostTypeDialogComponent> = this.myMatDialog.open(CostTypeDialogComponent, {
            width: '600px',
            data: isNullOrUndefined(costType) ? {} : costType,
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void this.myCostTypeFacade.fetchData();
            }
        });
    }

    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

    public nestedFilterCheck(search, data, key): string {
        if (typeof data[ key ] === 'object') {
            for (const k in data[ key ]) {
                if (data[ key ][ k ] !== null) {
                    search = this.nestedFilterCheck(search, data[ key ], k);
                }
            }
        } else {
            search += data[ key ];
        }
        return search;
    }

    public toggleExpand(group: ICostTypeGroupDetail): void {
        this.isExpandedMap[ group.id ] = !this.isExpandedMap[ group.id ];
    }

    public expandAll(): void {
        this.isExpandedMap = {};
        for (const group of this.dataSource.data) {
            this.isExpandedMap[ group.id ] = true;
        }
        this.isExpandAll = true;
    }

    public collapseAll(): void {
        this.isExpandedMap = {};
        this.isExpandAll = false;
    }


    public onOpenCostTypeGroupDialog(costTypeGroup?: ICostTypeGroup): void {
        const dialog: MatDialogRef<CostTypeGroupDialogComponent> = this.myMatDialog.open(CostTypeGroupDialogComponent, {
            width: '600px',
            data: isNullOrUndefined(costTypeGroup) ? {} : costTypeGroup,
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void this.myCostTypeFacade.fetchData();
            }
        });
    }

    public onDeleteCostTypeGroup(group: ICostTypeGroup): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            width: '600px',
            data: {
                message: 'app.page.cost-type.confirm-delete-group.label',
                messageParams: { groupName: group.name }
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myCostTypeFacade.deleteCostTypeGroup(group.id);
            }
        });
    }

    public onDeleteCostType(costType: ICostType): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            width: '600px',
            data: {
                message: 'app.page.cost-type.confirm-delete.label',
                messageParams: { name: costType.name }
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myCostTypeFacade.deleteCostType(costType.id);
            }
        });
    }

    public addCostTypeToGroup(groupId: number): void {
        // Open Dialog with pre-selected group assignment
        this.onOpenCostTypeDialog({ costTypeGroupId: groupId, isPrimary: true } as ICostType);
    }
}
